import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import { isOrderNumber, OrderNumberSchema } from "./order-number.js";

export const MEMBER_FUND_BUSINESS_METRIC_CONTRACT_VERSION =
  "MEMBER_FUND_BUSINESS_METRICS_V1" as const;

export const MEMBER_FUND_BUSINESS_METRIC_DETAIL_CONTRACT_VERSION =
  "MEMBER_FUND_BUSINESS_METRIC_DETAIL_V1" as const;
export const MEMBER_FUND_BUSINESS_METRIC_DETAIL_CAPACITY = 5_000;

export const MemberFundBusinessMetricIdSchema = Type.Union([
  Type.Literal("STORED_VALUE_PRINCIPAL"),
  Type.Literal("BONUS_VALUE"),
]);
export type MemberFundBusinessMetricId = Static<typeof MemberFundBusinessMetricIdSchema>;

export const MemberFundLedgerEntryNumberSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern: "^HZ-[0-9]{8}-[0-9A-F]{12}$",
});
export type MemberFundLedgerEntryNumber = Static<typeof MemberFundLedgerEntryNumberSchema>;

const MEMBER_FUND_NUMBER_SOURCE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{11,127}$/u;
const FNV_1A_64_OFFSET = 0xcbf29ce484222325n;
const FNV_1A_64_PRIME = 0x100000001b3n;
const LOWER_48_BITS = 0xffffffffffffn;

function deriveFortyEightBitHex(value: string): string {
  let hash = FNV_1A_64_OFFSET;
  for (const character of value) {
    hash ^= BigInt(character.codePointAt(0) ?? 0);
    hash = BigInt.asUintN(64, hash * FNV_1A_64_PRIME);
  }
  return (hash & LOWER_48_BITS).toString(16).toUpperCase().padStart(12, "0");
}

export function deriveMemberFundLedgerEntryNumber(
  businessDate: string,
  entryId: string,
): MemberFundLedgerEntryNumber {
  if (!isCanonicalDate(businessDate)) {
    throw new TypeError("businessDate must be a real calendar date in YYYY-MM-DD format");
  }
  if (
    !MEMBER_FUND_NUMBER_SOURCE_PATTERN.test(entryId) ||
    [...entryId].filter((character) => /[A-Za-z0-9]/u.test(character)).length < 12
  ) {
    throw new TypeError("entryId must contain at least 12 safe alphanumeric characters");
  }
  return `HZ-${businessDate.replaceAll("-", "")}-${deriveFortyEightBitHex(
    `HZ\0${businessDate}\0${entryId}`,
  )}`;
}

export function isMemberFundLedgerEntryNumber(
  value: unknown,
): value is MemberFundLedgerEntryNumber {
  if (!Value.Check(MemberFundLedgerEntryNumberSchema, value)) return false;
  const match = /^HZ-(\d{4})(\d{2})(\d{2})-[0-9A-F]{12}$/u.exec(value);
  return Boolean(match && isCanonicalDate(`${match[1]}-${match[2]}-${match[3]}`));
}

const MemberFundBusinessMetricValueSchema = Type.Object(
  {
    valueFen: Type.Integer({
      minimum: -Number.MAX_SAFE_INTEGER,
      maximum: Number.MAX_SAFE_INTEGER,
    }),
    factCount: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
  },
  { additionalProperties: false },
);

export const MemberFundBusinessMetricSnapshotSchema = Type.Object(
  {
    contractVersion: Type.Literal(MEMBER_FUND_BUSINESS_METRIC_CONTRACT_VERSION),
    tenantId: Type.String({ minLength: 1, maxLength: 128 }),
    storeId: Type.String({ minLength: 1, maxLength: 128 }),
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    timeZone: Type.String({ minLength: 1, maxLength: 80 }),
    businessDayCutover: Type.String({ pattern: "^(?:[01]\\d|2[0-3]):[0-5]\\d$" }),
    rangeStartedAt: Type.String({ minLength: 1, maxLength: 64 }),
    rangeEndedAt: Type.String({ minLength: 1, maxLength: 64 }),
    generatedAt: Type.String({ minLength: 1, maxLength: 64 }),
    coverage: Type.Literal("COMPLETE_LEDGER"),
    storedValuePrincipal: MemberFundBusinessMetricValueSchema,
    bonusValue: MemberFundBusinessMetricValueSchema,
  },
  { additionalProperties: false },
);

export type MemberFundBusinessMetricSnapshot = Static<
  typeof MemberFundBusinessMetricSnapshotSchema
>;

const MemberFundDetailIdentifierSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const MemberFundDetailMoneySchema = Type.Integer({
  minimum: -Number.MAX_SAFE_INTEGER,
  maximum: Number.MAX_SAFE_INTEGER,
});
const MemberFundDetailBalanceSchema = Type.Integer({
  minimum: 0,
  maximum: Number.MAX_SAFE_INTEGER,
});
const MemberFundDetailTransactionTypeSchema = Type.Union([
  Type.Literal("RECHARGE"),
  Type.Literal("CONSUMPTION"),
  Type.Literal("REFUND"),
  Type.Literal("ADJUSTMENT"),
]);
const MemberFundDetailBusinessTypeSchema = Type.Union([
  Type.Literal("STORED_VALUE"),
  Type.Literal("PROMOTIONAL"),
]);
const MemberFundDetailSharedItemProperties = {
  entryNumber: MemberFundLedgerEntryNumberSchema,
  memberNumber: Type.String({ minLength: 1, maxLength: 32 }),
  memberName: Type.String({ minLength: 1, maxLength: 80 }),
  businessType: MemberFundDetailBusinessTypeSchema,
  transactionType: MemberFundDetailTransactionTypeSchema,
  walletName: Type.String({ minLength: 1, maxLength: 40 }),
  deltaFen: MemberFundDetailMoneySchema,
  balanceBeforeFen: MemberFundDetailBalanceSchema,
  balanceAfterFen: MemberFundDetailBalanceSchema,
  orderNumber: Type.Union([OrderNumberSchema, Type.Null()]),
  reason: Type.String({ minLength: 1, maxLength: 200 }),
  occurredAt: Type.String({ minLength: 1, maxLength: 64 }),
} as const;

export const MemberFundBusinessMetricDetailSourceItemSchema = Type.Object(
  {
    entryId: MemberFundDetailIdentifierSchema,
    transactionId: MemberFundDetailIdentifierSchema,
    memberId: MemberFundDetailIdentifierSchema,
    actorId: MemberFundDetailIdentifierSchema,
    authorizerId: Type.Union([MemberFundDetailIdentifierSchema, Type.Null()]),
    ...MemberFundDetailSharedItemProperties,
    evidence: Type.Union([
      Type.Literal("SIMULATED"),
      Type.Literal("MANUAL_FALLBACK"),
      Type.Literal("STORE_ACCEPTED"),
    ]),
  },
  { additionalProperties: false },
);
export type MemberFundBusinessMetricDetailSourceItem = Static<
  typeof MemberFundBusinessMetricDetailSourceItemSchema
>;

export const MemberFundBusinessMetricDetailItemSchema = Type.Object(
  {
    ...MemberFundDetailSharedItemProperties,
    operatorDisplayName: Type.String({ minLength: 1, maxLength: 80 }),
    authorizerDisplayName: Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
    dataSource: Type.Union([Type.Literal("TEST_DATA"), Type.Literal("STORE_RECORD")]),
  },
  { additionalProperties: false },
);
export type MemberFundBusinessMetricDetailItem = Static<
  typeof MemberFundBusinessMetricDetailItemSchema
>;

const MemberFundBusinessMetricDetailEnvelopeProperties = {
  contractVersion: Type.Literal(MEMBER_FUND_BUSINESS_METRIC_DETAIL_CONTRACT_VERSION),
  tenantId: Type.String({ minLength: 1, maxLength: 128 }),
  storeId: Type.String({ minLength: 1, maxLength: 128 }),
  businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
  metricId: MemberFundBusinessMetricIdSchema,
  timeZone: Type.String({ minLength: 1, maxLength: 80 }),
  businessDayCutover: Type.String({ pattern: "^(?:[01]\\d|2[0-3]):[0-5]\\d$" }),
  rangeStartedAt: Type.String({ minLength: 1, maxLength: 64 }),
  rangeEndedAt: Type.String({ minLength: 1, maxLength: 64 }),
  generatedAt: Type.String({ minLength: 1, maxLength: 64 }),
  coverage: Type.Literal("COMPLETE_LEDGER"),
  valueFen: MemberFundDetailMoneySchema,
  factCount: Type.Integer({
    minimum: 0,
    maximum: MEMBER_FUND_BUSINESS_METRIC_DETAIL_CAPACITY,
  }),
} as const;

export const MemberFundBusinessMetricDetailSnapshotSchema = Type.Object(
  {
    ...MemberFundBusinessMetricDetailEnvelopeProperties,
    items: Type.Array(MemberFundBusinessMetricDetailSourceItemSchema, {
      maxItems: MEMBER_FUND_BUSINESS_METRIC_DETAIL_CAPACITY,
    }),
  },
  { additionalProperties: false },
);
export type MemberFundBusinessMetricDetailSnapshot = Static<
  typeof MemberFundBusinessMetricDetailSnapshotSchema
>;

export const MemberFundBusinessMetricDetailReadModelSchema = Type.Object(
  {
    ...MemberFundBusinessMetricDetailEnvelopeProperties,
    items: Type.Array(MemberFundBusinessMetricDetailItemSchema, {
      maxItems: MEMBER_FUND_BUSINESS_METRIC_DETAIL_CAPACITY,
    }),
  },
  { additionalProperties: false },
);
export type MemberFundBusinessMetricDetailReadModel = Static<
  typeof MemberFundBusinessMetricDetailReadModelSchema
>;

function isCanonicalDate(value: string): boolean {
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) && new Date(milliseconds).toISOString().slice(0, 10) === value
  );
}

function isCanonicalTimestamp(value: string): boolean {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

type MemberFundDetailEnvelope = Pick<
  MemberFundBusinessMetricDetailReadModel,
  | "tenantId"
  | "storeId"
  | "businessDate"
  | "metricId"
  | "timeZone"
  | "businessDayCutover"
  | "rangeStartedAt"
  | "rangeEndedAt"
  | "generatedAt"
  | "valueFen"
  | "factCount"
>;

type MemberFundDetailCommonItem = Pick<
  MemberFundBusinessMetricDetailItem,
  | "entryNumber"
  | "memberNumber"
  | "memberName"
  | "businessType"
  | "transactionType"
  | "walletName"
  | "deltaFen"
  | "balanceBeforeFen"
  | "balanceAfterFen"
  | "orderNumber"
  | "reason"
  | "occurredAt"
>;

function memberFundMetricBusinessType(
  metricId: MemberFundBusinessMetricId,
): MemberFundBusinessMetricDetailItem["businessType"] {
  return metricId === "STORED_VALUE_PRINCIPAL" ? "STORED_VALUE" : "PROMOTIONAL";
}

function memberFundDetailEnvelopeIsConsistent(value: MemberFundDetailEnvelope): boolean {
  if (
    !isCanonicalDate(value.businessDate) ||
    !isCanonicalTimestamp(value.rangeStartedAt) ||
    !isCanonicalTimestamp(value.rangeEndedAt) ||
    !isCanonicalTimestamp(value.generatedAt) ||
    Date.parse(value.rangeEndedAt) <= Date.parse(value.rangeStartedAt) ||
    (value.factCount === 0 && value.valueFen !== 0)
  ) {
    return false;
  }
  try {
    new Intl.DateTimeFormat("zh-CN", { timeZone: value.timeZone }).format(new Date(0));
  } catch {
    return false;
  }
  return true;
}

function memberFundDetailItemIsConsistent(
  item: MemberFundDetailCommonItem,
  envelope: MemberFundDetailEnvelope,
): boolean {
  const occurredAt = Date.parse(item.occurredAt);
  const expectsOrder = item.transactionType === "CONSUMPTION" || item.transactionType === "REFUND";
  return (
    isMemberFundLedgerEntryNumber(item.entryNumber) &&
    item.entryNumber.slice(3, 11) === envelope.businessDate.replaceAll("-", "") &&
    item.businessType === memberFundMetricBusinessType(envelope.metricId) &&
    isCanonicalTimestamp(item.occurredAt) &&
    occurredAt >= Date.parse(envelope.rangeStartedAt) &&
    occurredAt < Date.parse(envelope.rangeEndedAt) &&
    occurredAt <= Date.parse(envelope.generatedAt) &&
    item.deltaFen !== 0 &&
    item.balanceAfterFen === item.balanceBeforeFen + item.deltaFen &&
    Number.isSafeInteger(item.balanceAfterFen) &&
    expectsOrder === (item.orderNumber !== null) &&
    (item.orderNumber === null || isOrderNumber(item.orderNumber)) &&
    (item.transactionType !== "RECHARGE" || item.deltaFen > 0) &&
    (item.transactionType !== "CONSUMPTION" || item.deltaFen < 0) &&
    (item.transactionType !== "REFUND" || item.deltaFen > 0) &&
    item.memberNumber === item.memberNumber.trim() &&
    item.memberName === item.memberName.trim() &&
    item.walletName === item.walletName.trim() &&
    item.reason === item.reason.trim()
  );
}

function memberFundDetailItemsAreConsistent(
  items: readonly MemberFundDetailCommonItem[],
  envelope: MemberFundDetailEnvelope,
): boolean {
  if (items.length !== envelope.factCount) return false;
  const entryNumbers = new Set<string>();
  let valueFen = 0;
  let previousOccurredAt = Number.POSITIVE_INFINITY;
  let previousEntryNumber = "";
  for (const item of items) {
    const occurredAt = Date.parse(item.occurredAt);
    if (
      entryNumbers.has(item.entryNumber) ||
      !memberFundDetailItemIsConsistent(item, envelope) ||
      occurredAt > previousOccurredAt ||
      (occurredAt === previousOccurredAt &&
        previousEntryNumber !== "" &&
        item.entryNumber <= previousEntryNumber)
    ) {
      return false;
    }
    valueFen += item.deltaFen;
    if (!Number.isSafeInteger(valueFen)) return false;
    entryNumbers.add(item.entryNumber);
    previousOccurredAt = occurredAt;
    previousEntryNumber = item.entryNumber;
  }
  return valueFen === envelope.valueFen;
}

export function isMemberFundBusinessMetricDetailSnapshot(
  value: unknown,
  expectedScope?: {
    tenantId: string;
    storeId: string;
    businessDate: string;
    metricId: MemberFundBusinessMetricId;
  },
): value is MemberFundBusinessMetricDetailSnapshot {
  if (!Value.Check(MemberFundBusinessMetricDetailSnapshotSchema, value)) return false;
  if (
    !memberFundDetailEnvelopeIsConsistent(value) ||
    (expectedScope !== undefined &&
      (value.tenantId !== expectedScope.tenantId ||
        value.storeId !== expectedScope.storeId ||
        value.businessDate !== expectedScope.businessDate ||
        value.metricId !== expectedScope.metricId))
  ) {
    return false;
  }
  const entryIds = new Set<string>();
  const transactionFacts = new Map<string, string>();
  for (const item of value.items) {
    let expectedNumber: MemberFundLedgerEntryNumber;
    try {
      expectedNumber = deriveMemberFundLedgerEntryNumber(value.businessDate, item.entryId);
    } catch {
      return false;
    }
    if (entryIds.has(item.entryId) || item.entryNumber !== expectedNumber) return false;
    const transactionFact = JSON.stringify([
      item.memberId,
      item.memberNumber,
      item.memberName,
      item.transactionType,
      item.orderNumber,
      item.reason,
      item.actorId,
      item.authorizerId,
      item.occurredAt,
      item.evidence,
    ]);
    const existingTransactionFact = transactionFacts.get(item.transactionId);
    if (existingTransactionFact !== undefined && existingTransactionFact !== transactionFact) {
      return false;
    }
    entryIds.add(item.entryId);
    transactionFacts.set(item.transactionId, transactionFact);
  }
  return memberFundDetailItemsAreConsistent(value.items, value);
}

export function isMemberFundBusinessMetricDetailReadModel(
  value: unknown,
  expectedScope?: {
    tenantId: string;
    storeId: string;
    businessDate: string;
    metricId: MemberFundBusinessMetricId;
  },
): value is MemberFundBusinessMetricDetailReadModel {
  return (
    Value.Check(MemberFundBusinessMetricDetailReadModelSchema, value) &&
    memberFundDetailEnvelopeIsConsistent(value) &&
    (expectedScope === undefined ||
      (value.tenantId === expectedScope.tenantId &&
        value.storeId === expectedScope.storeId &&
        value.businessDate === expectedScope.businessDate &&
        value.metricId === expectedScope.metricId)) &&
    value.items.every(
      (item) =>
        item.operatorDisplayName === item.operatorDisplayName.trim() &&
        (item.authorizerDisplayName === null ||
          item.authorizerDisplayName === item.authorizerDisplayName.trim()),
    ) &&
    memberFundDetailItemsAreConsistent(value.items, value)
  );
}

export function isMemberFundBusinessMetricSnapshot(
  value: unknown,
  expectedScope?: { tenantId: string; storeId: string; businessDate: string },
): value is MemberFundBusinessMetricSnapshot {
  if (!Value.Check(MemberFundBusinessMetricSnapshotSchema, value)) return false;
  if (
    !isCanonicalDate(value.businessDate) ||
    !isCanonicalTimestamp(value.rangeStartedAt) ||
    !isCanonicalTimestamp(value.rangeEndedAt) ||
    !isCanonicalTimestamp(value.generatedAt) ||
    Date.parse(value.rangeEndedAt) <= Date.parse(value.rangeStartedAt) ||
    (value.storedValuePrincipal.factCount === 0 && value.storedValuePrincipal.valueFen !== 0) ||
    (value.bonusValue.factCount === 0 && value.bonusValue.valueFen !== 0) ||
    (expectedScope !== undefined &&
      (value.tenantId !== expectedScope.tenantId ||
        value.storeId !== expectedScope.storeId ||
        value.businessDate !== expectedScope.businessDate))
  ) {
    return false;
  }
  try {
    new Intl.DateTimeFormat("zh-CN", { timeZone: value.timeZone }).format(new Date(0));
  } catch {
    return false;
  }
  return true;
}
